/**
 * Agent-workflow progress contract (modes 4/5 research + verification).
 *
 * Structural mirror of the server's
 * `server/src/domain/schemas/rag/agent-workflow.schema.ts` — the tracker state
 * the multi-agent RAG answering pipeline broadcasts live
 * (`conversation_workflow_progress`) and persists as a `data-agent-workflow`
 * message part (the receipt) on the assistant turn.
 *
 * Realtime/SSE events are NOT part of the generated OpenAPI client, so this
 * copy is maintained by hand; keep it byte-compatible with the server schema.
 */

import { z } from "zod";

/** What a step is doing — the web maps this (+ loop) to a localized label. */
export const AgentWorkflowStepKindSchema = z.enum([
	"research",
	"draft",
	"verify",
	"revise",
]);
export type AgentWorkflowStepKind = z.infer<typeof AgentWorkflowStepKindSchema>;

export const AgentWorkflowStepStatusSchema = z.enum([
	"pending",
	"in-progress",
	"completed",
	"failed",
]);
export type AgentWorkflowStepStatus = z.infer<
	typeof AgentWorkflowStepStatusSchema
>;

/** One tracker row. `id` is unique within the run (e.g. "verify-2"). */
export const AgentWorkflowStepSchema = z.object({
	id: z.string().min(1).max(64),
	kind: AgentWorkflowStepKindSchema,
	status: AgentWorkflowStepStatusSchema,
	/** Verifier-loop iteration this step belongs to (1-based, mode 5 only). */
	loop: z.number().int().min(1).max(9).optional(),
	/** Evidence items collected so far (research steps). */
	evidenceCount: z.number().int().min(0).optional(),
	/** Claims the verifier flagged as unresolved (verify steps). */
	problemCount: z.number().int().min(0).optional(),
	/** The verifier's verdict for a completed verify step. */
	verdict: z.enum(["PASS", "NEEDS_REVISION"]).optional(),
});
export type AgentWorkflowStep = z.infer<typeof AgentWorkflowStepSchema>;

export const AgentWorkflowStatusSchema = z.enum([
	"running",
	"completed",
	"failed",
]);
export type AgentWorkflowStatus = z.infer<typeof AgentWorkflowStatusSchema>;

/**
 * The full tracker state. Emitted whole on every transition (steps are few) so
 * consumers replace, never merge — late/duplicate delivery is harmless.
 */
export const AgentWorkflowStateSchema = z.object({
	/** Unique id for this pipeline run (correlates live events ↔ receipt part). */
	id: z.string().min(1).max(64),
	/** The workflow family; only the RAG answering pipeline today. */
	workflow: z.literal("rag-answer"),
	/** Answering mode the run executes under ('4' research, '5' + verifier). */
	mode: z.enum(["4", "5"]),
	status: AgentWorkflowStatusSchema,
	steps: z.array(AgentWorkflowStepSchema).max(32),
	/** ISO timestamp when the pipeline started. */
	startedAt: z.string(),
	/** Wall-clock ms since start, stamped at each emission. */
	elapsedMs: z.number().int().min(0).optional(),
});
export type AgentWorkflowState = z.infer<typeof AgentWorkflowStateSchema>;

/**
 * The persisted message part carrying the finished run's receipt. Appended to
 * the assistant turn's parts by the reply composition root.
 */
export const DataAgentWorkflowPartSchema = z.object({
	type: z.literal("data-agent-workflow"),
	id: z.string().max(64).optional(),
	data: AgentWorkflowStateSchema,
});
export type DataAgentWorkflowPart = z.infer<typeof DataAgentWorkflowPartSchema>;

export function isAgentWorkflowState(v: unknown): v is AgentWorkflowState {
	return AgentWorkflowStateSchema.safeParse(v).success;
}

/** Narrow an unknown message part to the agent-workflow receipt part. */
export function isDataAgentWorkflowPart(
	part: unknown
): part is DataAgentWorkflowPart {
	return DataAgentWorkflowPartSchema.safeParse(part).success;
}
